import React from 'react'
import OllieIcon from 'src/components/icons/OllieIcon'
import styled from 'styled-components/macro'
import { OLLIE_FAB_ARIA_LABEL } from 'src/utils/ollieFabNotice'

/**
 * @param {{ notice?: { show: boolean, label: string|null, ariaLabel: string } }} props
 *   `notice` from `buildOllieFabNotice`: dot + "Ollie noticed N things" only
 *   when something is new to this person.
 */
const OllieFab = ({
  onClick,
  bottomOffset = 24,
  isOpen = false,
  ariaLabel = OLLIE_FAB_ARIA_LABEL,
  notice = null,
}) => {
  if (isOpen) {
    return null
  }

  const showNotice = Boolean(notice?.show && notice.label)

  return (
    <>
      {showNotice && (
        <NoticePill
          type='button'
          onClick={onClick}
          $bottomOffset={bottomOffset}
          tabIndex={-1}
          aria-hidden='true'
        >
          {notice.label}
        </NoticePill>
      )}
      <FabButton
        type='button'
        onClick={onClick}
        $bottomOffset={bottomOffset}
        aria-label={showNotice ? notice.ariaLabel : ariaLabel}
        title={showNotice ? notice.label : 'Ask Ollie'}
      >
        <OllieIcon size={30} variant='inverse' />
        {showNotice && <Dot data-testid='ollie-fab-dot' />}
      </FabButton>
    </>
  )
}

const Dot = styled.span`
  position: absolute;
  top: 3px;
  right: 3px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colours.chilliRed};
  border: 2px solid ${({ theme }) => theme.colours.mayoWhite};
  pointer-events: none;
`

const NoticePill = styled.button`
  position: fixed;
  right: 90px;
  bottom: ${({ $bottomOffset }) => `${$bottomOffset + 14}px`};
  height: 28px;
  padding: 0 12px;
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  border-radius: 14px;
  background: ${({ theme }) => theme.colours.mayoWhite};
  color: ${({ theme }) => theme.colours.bbqBlack};
  font-size: 13px;
  font-weight: 600;
  line-height: 26px;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(19, 45, 7, 0.16);
  z-index: 1050;
  transition: bottom 0.2s ease;

  @media (max-width: 480px) {
    display: none;
  }
`

const FabButton = styled.button`
  position: fixed;
  right: 24px;
  bottom: ${({ $bottomOffset }) => `${$bottomOffset}px`};
  width: 56px;
  height: 56px;
  border: none;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: ${({ theme }) => theme.colours.lettuceGreen};
  color: ${({ theme }) => theme.colours.mayoWhite};
  box-shadow: 0 4px 14px rgba(19, 45, 7, 0.28);
  z-index: 1050;
  transition: bottom 0.2s ease, transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: scale(1.04);
    box-shadow: 0 6px 18px rgba(19, 45, 7, 0.34);
    background: ${({ theme }) => theme.colours.avocadoGreen};
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 480px) {
    right: 16px;
    width: 52px;
    height: 52px;
  }
`

export default OllieFab
